/**
 * Copyright (C) 2020 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React, { useRef } from 'react';
import styled from 'styled-components';
import StyledProps from '../theme/AppThemeInterface';
import { shallowEqual, useSelector } from 'react-redux';
import { selectTerminalsInYourBeamPanelShown } from '../../../store';
import { useClickAwayListener } from '../../../utils/GlobalUtils';
import { TERMINALS_IN_YOUR_BEAM_PANEL_WIDTH } from './TerminalsInYourBeamPanelContainer';
import ErrorBoundary from '../layout/ErrorBoundary';

interface OverlaysWrapperProps {
  top: string;
  width?: string;
  shown: boolean;
  hidePanel: Function;
  queryParamsToNotHidePanel: string[];
  headerTitle: string;
  children: JSX.Element | (JSX.Element | null)[] | null | undefined;
}

const StyledPanel = styled.div`
  background-color: ${({ theme }: StyledProps) => theme.primaryBackgroundColor};
  box-shadow: 2px 2px 4px 0 rgba(0, 0, 0, 0.44);
  right: 48px;

  position: absolute;
  top: ${({ top }: OverlaysWrapperProps) => top};
  z-index: 1;

  overflow: hidden;
  width: ${({ width }: OverlaysWrapperProps) => (width ? width : '322px')};
  padding-bottom: 15px; // Needed to set the spacing at the bottom of the last section since section spacing is only set on top

  .overlays__panel__header {
    color: white;
    opacity: 0.87;
    font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
    font-size: 15px;
    letter-spacing: 0.14px;
    line-height: 15px;

    position: relative;
    margin: 15px 16px 15px;
  }

  hr {
    border-color: black;
    border: 0;
    border-top: 1px solid black;
    margin: 0 16px 0;
  }
`;

export function OverlaysWrapper({
  shown,
  queryParamsToNotHidePanel,
  headerTitle,
  children,
  hidePanel,
  top,
  width,
}: OverlaysWrapperProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const terminalsInYourBeamPanelShown = useSelector(selectTerminalsInYourBeamPanelShown, shallowEqual);

  useClickAwayListener(
    panelRef,
    () => {
      if (shown) {
        hidePanel();
      }
    },
    ...queryParamsToNotHidePanel,
  );

  return shown ? (
    <StyledPanel
      ref={panelRef}
      terminalsInYourBeamPanelShown={terminalsInYourBeamPanelShown}
      terminalsInYourBeamPanelWidthPx={TERMINALS_IN_YOUR_BEAM_PANEL_WIDTH}
      top={top}
      width={width}
    >
      <ErrorBoundary
        message="Error in the map panel"
        onReset={() => {
          hidePanel();
        }}
      >
        <div className="overlays__panel__header">{headerTitle}</div>
        {children}
      </ErrorBoundary>
    </StyledPanel>
  ) : null;
}

export default OverlaysWrapper;
